import { onDestroy } from "@/modules/_";
import gsap, { reduced } from "@lib/gsap";
import { gsapEase } from "@/utils/easings";

// Careers department list. Two behaviors:
//  1) Single-open accordion — the answer panel animates its height 0 ↔ auto.
//     answer-inner is made a BFC (overflow:hidden) so its child margins are
//     contained → the measured/auto height stays stable → no jump on open.
//  2) Hover reveal — the floating visual-stack bubbles scale + clip-path in
//     with a stagger on hover, and reverse out.
const DURATION = 0.5;
const EASE = "power3.out";

// visual-stack hover reveal
const VISUAL_IN = 0.5;
const VISUAL_OUT = 0.35;
const VISUAL_STAGGER = 0.06;
const VISUAL_HIDDEN = { scale: 0, clipPath: "inset(50% round 50%)" };
const VISUAL_SHOWN = { scale: 1, clipPath: "inset(0% round 50%)" };

// ── Random team photos for the visual-stack bubbles ──────────────────────────
// A hidden CMS list (dept-list="img-cms-list", paginated at 50) is the photo
// pool; on mount we drop a random photo into each visual-item's <img> so the
// hover bubbles show real faces. Webflow gotcha: setting img.src alone won't
// repaint while a stale srcset/sizes lingers — overwrite all three.
const visualImg = (v: HTMLElement): HTMLImageElement | null =>
  v instanceof HTMLImageElement ? v : v.querySelector<HTMLImageElement>("img");

const applyPhoto = (target: HTMLImageElement, source: HTMLImageElement) => {
  target.srcset = source.srcset;
  target.sizes = source.sizes;
  target.src = source.src;
  if (source.alt) target.alt = source.alt;
};

// Fisher–Yates shuffle of a copy → `count` photos, distinct while the pool
// lasts, wrapping only if a stack needs more bubbles than there are photos.
const pickPhotos = (
  pool: HTMLImageElement[],
  count: number,
): HTMLImageElement[] => {
  const bag = [...pool];
  for (let i = bag.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [bag[i], bag[j]] = [bag[j], bag[i]];
  }
  return Array.from({ length: count }, (_, i) => bag[i % bag.length]);
};

let uid = 0; // unique-id seed across instances (for aria-controls)

export default function (element: HTMLElement, _dataset: DOMStringMap) {
  const items = Array.from(
    element.querySelectorAll<HTMLElement>('[dept-list="item"]'),
  );
  if (!items.length) return;

  const dur = reduced ? 0 : DURATION;
  const instanceId = uid++;

  // Photo pool from the hidden CMS list — collected once, shared across rows.
  // Look inside this instance first, then fall back to anywhere on the page.
  const cmsList =
    element.querySelector<HTMLElement>('[dept-list="img-cms-list"]') ||
    document.querySelector<HTMLElement>('[dept-list="img-cms-list"]');
  if (cmsList) cmsList.style.display = "none"; // data source only — never shown
  const photoPool = cmsList
    ? Array.from(cmsList.querySelectorAll<HTMLImageElement>("img")).filter(
        (img) => img.src || img.srcset,
      )
    : [];

  type Entry = {
    item: HTMLElement;
    trigger: HTMLElement;
    answer: HTMLElement;
    visuals: HTMLElement[];
  };

  const entries: Entry[] = [];
  let openIndex = -1;

  const setOpen = (i: number, open: boolean, immediate = false) => {
    const { item, trigger, answer } = entries[i];
    item.classList.toggle("is-open", open);
    trigger.setAttribute("aria-expanded", String(open));
    answer.setAttribute("aria-hidden", String(!open));

    gsap.to(answer, {
      height: open ? "auto" : 0,
      duration: immediate ? 0 : dur,
      ease: EASE,
      overwrite: true,
    });
  };

  const toggle = (i: number) => {
    if (openIndex === i) {
      setOpen(i, false);
      openIndex = -1;
      return;
    }
    if (openIndex !== -1) setOpen(openIndex, false); // only one open at a time
    setOpen(i, true);
    openIndex = i;
  };

  const unsubs: Array<() => void> = [];

  items.forEach((item) => {
    const trigger = item.querySelector<HTMLElement>('[dept-list="trigger"]');
    const answer = item.querySelector<HTMLElement>('[dept-list="answer"]');
    if (!trigger || !answer) return;

    const inner = item.querySelector<HTMLElement>('[dept-list="answer-inner"]');
    const stack = item.querySelector<HTMLElement>('[dept-list="visual-stack"]');
    const visuals = Array.from(
      item.querySelectorAll<HTMLElement>('[dept-list="visual-item"]'),
    );

    // Animate the answer's height; make the inner a BFC so its child margins
    // are contained → measured auto height stays stable → no jump on open.
    gsap.set(answer, { height: 0 });
    if (inner) gsap.set(inner, { overflow: "hidden" });

    // Visual stack is decorative and must not eat clicks/hover on the row.
    if (stack) {
      stack.setAttribute("aria-hidden", "true");
      stack.style.pointerEvents = "none";
    }
    if (visuals.length) {
      gsap.set(visuals, { ...VISUAL_HIDDEN, transformOrigin: "center center" });

      // Fill this row's bubbles with random team photos from the pool.
      if (photoPool.length) {
        const picks = pickPhotos(photoPool, visuals.length);
        visuals.forEach((v, k) => {
          const img = visualImg(v);
          if (img && picks[k]) applyPhoto(img, picks[k]);
        });
      }
    }

    // A11y: make the trigger div behave like a disclosure button.
    const panelId = answer.id || `dept-list-${instanceId}-${entries.length}`;
    answer.id = panelId;
    trigger.setAttribute("role", "button");
    trigger.setAttribute("tabindex", "0");
    trigger.setAttribute("aria-controls", panelId);
    trigger.setAttribute("aria-expanded", "false");
    answer.setAttribute("aria-hidden", "true");
    trigger.style.cursor = "pointer";

    entries.push({ item, trigger, answer, visuals });
    const index = entries.length - 1;

    const onClick = () => toggle(index);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault(); // Space would otherwise scroll the page
        toggle(index);
      }
    };
    trigger.addEventListener("click", onClick);
    trigger.addEventListener("keydown", onKey);

    // Hover reveal — bubbles pop in with a stagger, reverse (from end) on out.
    const showVisuals = () => {
      if (!visuals.length) return;
      gsap.to(visuals, {
        ...VISUAL_SHOWN,
        duration: reduced ? 0 : VISUAL_IN,
        ease: gsapEase("pop"),
        stagger: reduced ? 0 : VISUAL_STAGGER,
        overwrite: true,
      });
    };
    const hideVisuals = () => {
      if (!visuals.length) return;
      gsap.to(visuals, {
        ...VISUAL_HIDDEN,
        duration: reduced ? 0 : VISUAL_OUT,
        ease: "power2.in",
        stagger: reduced ? 0 : { each: VISUAL_STAGGER, from: "end" },
        overwrite: true,
      });
    };
    item.addEventListener("mouseenter", showVisuals);
    item.addEventListener("mouseleave", hideVisuals);

    unsubs.push(() => {
      trigger.removeEventListener("click", onClick);
      trigger.removeEventListener("keydown", onKey);
      item.removeEventListener("mouseenter", showVisuals);
      item.removeEventListener("mouseleave", hideVisuals);
    });
  });

  // Open the first item by default (instant, no load flash) as an affordance.
  if (entries.length) {
    setOpen(0, true, true);
    openIndex = 0;
  }

  onDestroy(() => {
    unsubs.forEach((off) => off());
    entries.forEach(({ answer, visuals }) => {
      gsap.killTweensOf(answer);
      if (visuals.length) gsap.killTweensOf(visuals);
    });
  });
}
